import React from 'react';
import { Chip, Tooltip, Box } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { isVersionOutdated, getVersionMetadata } from './questionnaireVersionUtils';

/**
 * QuestionnaireVersionBadge - Displays the version of the questionnaire template being used
 * Provides visual feedback about whether the version is current or outdated
 */
const QuestionnaireVersionBadge = ({ version, showTooltip = true, size = "small" }) => {
  if (!version) return null;

  const metadata = getVersionMetadata(version);
  const isOutdated = isVersionOutdated(version);
  const isCurrent = metadata?.isCurrent;

  const getChipProps = () => {
    if (isCurrent) {
      return {
        color: 'primary',
        variant: 'filled',
        label: `v${version} (Current)`
      };
    } else if (isOutdated) {
      return {
        color: 'warning',
        variant: 'outlined',
        label: `v${version} (Legacy)`
      };
    } else {
      return {
        color: 'default',
        variant: 'outlined',
        label: `v${version}`
      };
    }
  };

  const getTooltipText = () => {
    if (isCurrent) {
      return `This questionnaire uses the current template version (${version}). All features are available.`;
    } else if (isOutdated) {
      return `This questionnaire uses an older template version (${version}). Some newer features may not be available, but all data will be preserved.`;
    } else {
      return `Template version: ${version}`;
    }
  };

  const chipProps = getChipProps();
  
  const chip = (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <Chip
        {...chipProps}
        size={size}
        icon={showTooltip ? <InfoIcon /> : undefined}
        sx={{
          fontSize: size === 'small' ? '0.75rem' : '0.875rem',
          height: size === 'small' ? 24 : 32,
          '& .MuiChip-icon': {
            fontSize: '1rem'
          }
        }}
      />
    </Box>
  );

  if (showTooltip) {
    return (
      <Tooltip title={getTooltipText()} arrow placement="top">
        {chip}
      </Tooltip>
    );
  }

  return chip;
};

export default QuestionnaireVersionBadge;
